/**
 * Messages that travel on the data channel.
 *
 * The channel is unordered and unreliable, so every message carries a sequence
 * number and receivers drop anything staler than what they already have. That
 * is the entire delivery guarantee, deliberately: a dropped input or snapshot is
 * replaced by the next one 16 or 33 ms later.
 */
import type { BlobInput, SimState, Side } from '@office/sim';
import type { LobbyIntent, LobbyState } from './lobby.js';

/** Full authoritative state, host -> guest, 30 Hz. */
export interface SnapshotMessage {
  t: 'snap';
  seq: number;
  /** Host clock in ms when the snapshot was taken, for latency estimates. */
  sent: number;
  state: SimState;
}

/** Guest input, guest -> host, 60 Hz. */
export interface InputMessage {
  t: 'input';
  seq: number;
  /** Sim tick the guest believed was current. Unused until reconciliation. */
  tick: number;
  input: BlobInput;
}

/** Host -> everyone whenever the lobby changes. */
export interface LobbyMessage {
  t: 'lobby';
  state: LobbyState;
}

/** Guest -> host: a request to change name, side or ready flag. */
export interface IntentMessage {
  t: 'intent';
  intent: LobbyIntent;
}

/** Host -> everyone when the match begins. */
export interface StartMessage {
  t: 'start';
  game: string;
  /** Slot this recipient drives, and the side layout of every slot. */
  slot: number;
  sides: Side[];
  /** The host's tuning constants, so both ends draw the same court. */
  constants: Record<string, number>;
}

/** Host -> everyone when the match ends and the lobby reopens. */
export interface EndMessage {
  t: 'end';
}

/** Either direction, for a round-trip time estimate. */
export interface PingMessage {
  t: 'ping';
  sent: number;
}

export interface PongMessage {
  t: 'pong';
  sent: number;
}

export type NetMessage =
  | SnapshotMessage
  | InputMessage
  | LobbyMessage
  | IntentMessage
  | StartMessage
  | EndMessage
  | PingMessage
  | PongMessage;

const KINDS = new Set(['snap', 'input', 'lobby', 'intent', 'start', 'end', 'ping', 'pong']);

export function encode(message: NetMessage): string {
  return JSON.stringify(message);
}

/** Returns null for anything unparseable rather than throwing on the hot path. */
export function decode(raw: string): NetMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const t = (parsed as { t?: unknown }).t;
  if (typeof t !== 'string' || !KINDS.has(t)) return null;
  return parsed as NetMessage;
}

/**
 * Tracks the highest sequence number seen so a reordered delivery can be
 * dropped instead of rewinding state.
 */
export class SequenceGate {
  private highest = -1;

  /** True if `seq` is newer than everything seen so far. */
  accept(seq: number): boolean {
    if (seq <= this.highest) return false;
    this.highest = seq;
    return true;
  }

  get latest(): number {
    return this.highest;
  }

  reset(): void {
    this.highest = -1;
  }
}
